import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout, BlockedBanner } from "./ClientHome";
import { toast } from "sonner";
import { Send, Star, ClipboardCheck } from "lucide-react";

const QUESTIONS = [
  { key: "q1", type: "text" },
  { key: "q2", type: "rating" },
  { key: "q3", type: "text" },
  { key: "q4", type: "text" },
  { key: "q5", type: "text" },
  { key: "q6", type: "text" },
];

export default function Feedback() {
  const { t } = useLang();
  const [overview, setOverview] = useState(null);
  const [answers, setAnswers] = useState({ q1: "", q2: 0, q3: "", q4: "", q5: "", q6: "" });
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/client/overview").then((r) => setOverview(r.data)).catch(() => setOverview({ user: { blocked: true } }));
    api.get("/client/feedback").then((r) => {
      if (r.data.length) {
        setExisting(r.data[0]);
        setAnswers((prev) => ({ ...prev, ...r.data[0].answers }));
      }
    }).catch(() => {});
  }, []);

  const answeredCount = QUESTIONS.filter((q) =>
    q.type === "rating" ? answers[q.key] >= 1 : (answers[q.key] || "").trim()
  ).length;
  const pct = Math.round((answeredCount / QUESTIONS.length) * 100);

  const submit = async () => {
    if (answeredCount < QUESTIONS.length) {
      toast.error(t("fb.missing"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/client/feedback", { phase_idx: overview?.current_phase_idx ?? 0, answers });
      toast.success(t("fb.sent"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!overview) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;
  if (overview.user?.blocked) return <ClientLayout><BlockedBanner /></ClientLayout>;

  return (
    <ClientLayout>
      <div className="max-w-xl mx-auto w-full space-y-4" data-testid="feedback-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-black uppercase tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-primary" /> {t("fb.title")}
          </h1>
          <span className="font-mono text-sm text-primary font-bold" data-testid="feedback-progress-label">{pct}%</span>
        </div>
        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="feedback-progress-bar" />
        </div>

        <div className="kc-card border-accent/40 p-5 kc-animate" data-testid="feedback-intro">
          <h2 className="text-xl font-bold mb-2">{t("fb.title")}</h2>
          <p className="text-sm text-muted-foreground italic leading-relaxed">{t("fb.intro")}</p>
        </div>

        {existing && (
          <p className="text-xs text-accent" data-testid="feedback-already-sent">
            {t("fb.alreadySent")} — {existing.submitted_at?.slice(0, 10)}
          </p>
        )}

        {QUESTIONS.map((q, i) => (
          <div key={q.key} className="kc-card p-5 kc-animate" style={{ animationDelay: `${0.06 * (i + 1)}s` }} data-testid={`feedback-question-${q.key}`}>
            <div className="flex items-start gap-3 mb-4">
              <span className="w-8 h-8 rounded-lg bg-accent/20 text-accent font-bold grid place-items-center shrink-0 text-sm">
                {i + 1}
              </span>
              <p className="font-bold flex-1 leading-snug">{t(`fb.${q.key}`)}</p>
              <Star className="w-4 h-4 text-primary shrink-0 fill-primary" />
            </div>
            {q.type === "rating" ? (
              <div className="grid grid-cols-5 gap-2" data-testid="feedback-rating-scale">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button key={n} data-testid={`feedback-rating-${n}`} onClick={() => setAnswers({ ...answers, q2: n })}
                    className={`h-11 rounded-lg font-mono font-bold transition-all active:scale-95 ${
                      answers.q2 === n ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/70"
                    }`}>
                    {n}
                  </button>
                ))}
              </div>
            ) : (
              <textarea data-testid={`feedback-answer-${q.key}`} rows={3} placeholder={t("fb.placeholder")}
                value={answers[q.key]} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                className="kc-input" />
            )}
          </div>
        ))}

        <button data-testid="feedback-submit-button" onClick={submit} disabled={saving}
          className="kc-btn-primary w-full flex items-center justify-center gap-2 !py-4 text-base sticky bottom-20">
          <Send className="w-5 h-5" /> {saving ? t("common.loading") : t("fb.submit")}
        </button>
      </div>
    </ClientLayout>
  );
}
